/**
 * Pure helpers for ephemeral-message pagination / empty-state resolution.
 */

export type PageLike<T extends { id: string }> = T[];

/** Oldest message id in a newest-first list (ulid-sorted). */
export function oldestId<T extends { id: string }>(
  messages: PageLike<T>,
): string | undefined {
  if (!messages.length) return undefined;
  return messages[messages.length - 1]?.id;
}

/** Newest message id in a newest-first list. */
export function newestId<T extends { id: string }>(
  messages: PageLike<T>,
): string | undefined {
  return messages[0]?.id;
}

/**
 * Whether a fetch that returned `rawCount` items (limit `limit`) reached the
 * start/end of history.
 */
export function pageExhausted(rawCount: number, limit: number): boolean {
  return rawCount < limit;
}

/**
 * After filtering expired messages from a raw page, decide if we must keep
 * paging older to find visible content or resolve an empty conversation.
 */
export function shouldContinuePastExpiredPage(options: {
  rawCount: number;
  visibleCount: number;
  limit: number;
  pagesFetched: number;
  maxPages: number;
}): "continue" | "resolve-empty" | "stop" {
  const { rawCount, visibleCount, limit, pagesFetched, maxPages } = options;

  if (visibleCount > 0) return "stop";
  if (pageExhausted(rawCount, limit) || rawCount === 0) return "resolve-empty";
  if (pagesFetched >= maxPages) return "resolve-empty";
  return "continue";
}

/**
 * Cache restore policy: never restore an empty mid-history view that would
 * leave the UI stuck on a loading skeleton.
 */
export function normalizeCachedChannelState<T>(state: {
  messages: T[];
  atStart: boolean;
  atEnd: boolean;
  scrollTop?: number;
}): {
  messages: T[];
  atStart: boolean;
  atEnd: boolean;
  scrollTop?: number;
} {
  if (state.messages.length === 0) {
    return {
      messages: [],
      atStart: true,
      atEnd: true,
    };
  }
  return state;
}
